import {
    CheckCircleRounded,
    CloseRounded,
    ExpandMoreRounded,
    SearchRounded,
} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    IconButton,
    LinearProgress,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import AcxDrawer from "components/UI/Drawer/AcxDrawer";
import React, { useMemo, useState } from "react";
import {
    KboAnalysisDimensionResult,
    KboAnalysisMaterial,
    KboCohortDefinition,
    KboCohortFilter,
    KboMultiLevelAnalysisPass,
    KboMultiLevelAnalysisResult,
    KboMultiLevelFinding,
} from "./KboFlowRunDebug";
import {
    Field,
    formatDate,
    formatNumber,
    renderable,
    SectionHeading,
    SectionStack,
    shortId,
} from "./KboFlowDebugUI";

const EVIDENCE_PAGE_SIZE = 10;

function formatPercent(value?: number): string {
    if (typeof value !== "number") return "—";
    return new Intl.NumberFormat(undefined, {
        style: "percent",
        maximumFractionDigits: 2,
    }).format(value);
}

function renderUnknown(value: unknown): React.ReactNode {
    if (value === null || value === undefined || value === "") return "—";
    if (React.isValidElement(value)) return value;
    if (typeof value === "object") return JSON.stringify(value);
    if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
    ) {
        return String(value);
    }
    return "—";
}

function FilterTable({ filters }: { filters?: KboCohortFilter[] }) {
    if (!filters?.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                No filters recorded.
            </Typography>
        );
    }

    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Field</TableCell>
                        <TableCell>Operator</TableCell>
                        <TableCell>Value</TableCell>
                        <TableCell>Reason</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filters.map((filter, index) => (
                        <TableRow
                            key={`${filter.alias}-${filter.operator}-${index}`}
                        >
                            <TableCell>{renderable(filter.alias)}</TableCell>
                            <TableCell>{renderable(filter.operator)}</TableCell>
                            <TableCell>{renderUnknown(filter.value)}</TableCell>
                            <TableCell>{renderable(filter.reason)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function CohortPanel({
    title,
    cohort,
    count,
}: {
    title: string;
    cohort?: KboCohortDefinition;
    count?: number;
}) {
    return (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            <Box
                sx={{
                    alignItems: "center",
                    backgroundColor: "gray.100",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    px: 1.5,
                    py: 1,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                {typeof count === "number" && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${formatNumber(count)} conversations`}
                    />
                )}
                {cohort?.name && (
                    <Chip size="small" variant="outlined" label={cohort.name} />
                )}
                {cohort?.role && (
                    <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={cohort.role}
                    />
                )}
            </Box>
            <Box sx={{ p: 1.5 }}>
                {(cohort?.startDateUtc || cohort?.endDateUtc) && (
                    <Field
                        label="Date range"
                        value={`${formatDate(
                            cohort.startDateUtc,
                        )} → ${formatDate(cohort.endDateUtc)}`}
                    />
                )}
                <Field label="Rationale" value={cohort?.rationale} />
                <Box sx={{ mt: 1 }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 0.5 }}
                    >
                        Filters
                    </Typography>
                    <FilterTable filters={cohort?.filters} />
                </Box>
            </Box>
        </Paper>
    );
}

export function resultValue(
    pass: KboMultiLevelAnalysisPass,
    result: KboAnalysisDimensionResult,
    index: number,
): React.ReactNode {
    if (result.distribution?.value !== undefined) {
        return renderUnknown(result.distribution.value);
    }

    const dimensionAlias = pass.dimension?.alias;
    const matchingFilter = result.cohort?.filters?.find(
        (filter) => filter.alias === dimensionAlias,
    );
    return renderUnknown(matchingFilter?.value ?? `Result ${index + 1}`);
}

function findSelectedFinding(
    pass: KboMultiLevelAnalysisPass,
): KboMultiLevelFinding | undefined {
    const selectedId = pass.selection?.findingId;
    if (!selectedId) return undefined;
    return pass.results
        ?.flatMap((result) => result.findings ?? [])
        .find((finding) => finding.findingId === selectedId);
}

type EvidenceSelection = {
    title: string;
    material: KboAnalysisMaterial[];
};

function EvidenceDrawer({
    selection,
    page,
    onPageChange,
    onClose,
}: {
    selection: EvidenceSelection | null;
    page: number;
    onPageChange: (page: number) => void;
    onClose: () => void;
}) {
    const pageCount = Math.max(
        1,
        Math.ceil((selection?.material.length ?? 0) / EVIDENCE_PAGE_SIZE),
    );
    const pageItems =
        selection?.material.slice(
            (page - 1) * EVIDENCE_PAGE_SIZE,
            page * EVIDENCE_PAGE_SIZE,
        ) ?? [];

    return (
        <AcxDrawer
            anchor="right"
            open={selection !== null}
            onDrawerClose={onClose}
            collapsible="full"
            disableEnforceFocus
            customRootStyles={{ zIndex: 1400, whiteSpace: "normal" }}
            customPaperSx={{
                maxWidth: "100vw",
                width: { xs: "100vw", sm: 680 },
            }}
            content={
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        maxWidth: "100vw",
                        width: { xs: "100vw", sm: 680 },
                    }}
                >
                    <Box
                        sx={{
                            alignItems: "flex-start",
                            backgroundColor: "background.paper",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            gap: 1,
                            p: 2,
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6">
                                Analyzed conversations
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ wordBreak: "break-word" }}
                            >
                                {selection?.title}
                            </Typography>
                            <Chip
                                size="small"
                                variant="outlined"
                                sx={{ mt: 0.75 }}
                                label={`${formatNumber(
                                    selection?.material.length,
                                )} records`}
                            />
                        </Box>
                        <IconButton
                            size="small"
                            aria-label="Close analyzed conversations"
                            onClick={onClose}
                        >
                            <CloseRounded />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                        <SectionStack>
                            {pageItems.map((item, index) => (
                                <Paper
                                    key={`${item.conversationId}-${index}`}
                                    variant="outlined"
                                    sx={{ p: 1.5 }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: "block",
                                            mb: 0.75,
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {item.conversationId ?? "Unknown conversation"}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {renderable(item.text)}
                                    </Typography>
                                </Paper>
                            ))}
                            {pageItems.length === 0 && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No analysis material was recorded.
                                </Typography>
                            )}
                        </SectionStack>
                    </Box>
                    {pageCount > 1 && (
                        <Box
                            sx={{
                                borderTop: "1px solid",
                                borderColor: "divider",
                                display: "flex",
                                justifyContent: "center",
                                p: 1.5,
                            }}
                        >
                            <Pagination
                                count={pageCount}
                                page={Math.min(page, pageCount)}
                                onChange={(_event, nextPage) =>
                                    onPageChange(nextPage)
                                }
                                color="primary"
                            />
                        </Box>
                    )}
                </Box>
            }
        />
    );
}

function FindingsForResult({
    pass,
    result,
    resultIndex,
}: {
    pass: KboMultiLevelAnalysisPass;
    result: KboAnalysisDimensionResult;
    resultIndex: number;
}) {
    const findings = result.findings ?? [];
    const selectedId = pass.selection?.findingId;
    const reviewedMaterialCount =
        result.reviewedMaterialCount ?? result.analysisMaterial?.length ?? 0;
    const reviewedMaterialCoverage = result.reviewedMaterialCoverage;

    return (
        <Card variant="outlined">
            <CardContent>
                <Box
                    sx={{
                        alignItems: "center",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                        mb: 1.5,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {resultValue(pass, result, resultIndex)}
                    </Typography>
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${formatNumber(
                            result.distribution?.count,
                        )} in cohort`}
                    />
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${formatNumber(
                            reviewedMaterialCount,
                        )} reviewed`}
                    />
                    {typeof reviewedMaterialCoverage === "number" && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`${formatPercent(reviewedMaterialCoverage)} reviewed`}
                        />
                    )}
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${findings.length} finding${
                            findings.length === 1 ? "" : "s"
                        }`}
                    />
                </Box>
                <SectionStack>
                    {result.cohort && (
                        <Accordion
                            disableGutters
                            sx={{
                                "&:before": { display: "none" },
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: "4px !important",
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreRounded />}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700 }}
                                >
                                    Dimension result cohort
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <CohortPanel
                                    title="Applied cohort"
                                    cohort={result.cohort}
                                    count={result.distribution?.count}
                                />
                            </AccordionDetails>
                        </Accordion>
                    )}
                    {findings.map((finding, findingIndex) => {
                        const selected =
                            !!selectedId && finding.findingId === selectedId;
                        return (
                            <Paper
                                key={finding.findingId ?? findingIndex}
                                variant="outlined"
                                sx={{
                                    borderColor: selected
                                        ? "success.main"
                                        : undefined,
                                    borderWidth: selected ? 2 : 1,
                                    p: 1.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        alignItems: "center",
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.75,
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ flex: 1, fontWeight: 700 }}
                                    >
                                        Finding {findingIndex + 1}
                                    </Typography>
                                    {selected && (
                                        <Chip
                                            size="small"
                                            color="success"
                                            icon={<CheckCircleRounded />}
                                            label="Selected this pass"
                                        />
                                    )}
                                    {finding.findingId && (
                                        <Tooltip title={finding.findingId}>
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={shortId(
                                                    finding.findingId,
                                                )}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>
                                <Field label="Claim" value={finding.claim} />
                                <Field
                                    label="Reasoning"
                                    value={finding.reasoning}
                                />
                                {!!finding.representativeConversationIds
                                    ?.length && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Representative conversations
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 0.5,
                                                mt: 0.5,
                                            }}
                                        >
                                            {finding.representativeConversationIds.map(
                                                (conversationId) => (
                                                    <Tooltip
                                                        key={conversationId}
                                                        title={conversationId}
                                                    >
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label={shortId(
                                                                conversationId,
                                                            )}
                                                        />
                                                    </Tooltip>
                                                ),
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </Paper>
                        );
                    })}
                    {findings.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            No findings were recorded for this dimension value.
                        </Typography>
                    )}
                </SectionStack>
            </CardContent>
        </Card>
    );
}

function PassAccordion({
    pass,
    questionIndex,
    originalIndex,
    displayIndex,
    onOpenEvidence,
}: {
    pass: KboMultiLevelAnalysisPass;
    questionIndex: number;
    originalIndex: number;
    displayIndex: number;
    onOpenEvidence: (
        pass: KboMultiLevelAnalysisPass,
        result: KboAnalysisDimensionResult,
        resultIndex: number,
    ) => void;
}) {
    const results = pass.results ?? [];
    const selectedFinding = findSelectedFinding(pass);
    const level = pass.level ?? displayIndex;
    const accordionId = `${questionIndex}-${
        pass.passId ?? originalIndex
    }`;

    return (
        <Accordion
            defaultExpanded={displayIndex === 0}
            disableGutters
            sx={{
                "&:before": { display: "none" },
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "4px !important",
                overflow: "hidden",
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreRounded />}
                aria-controls={`analysis-pass-${accordionId}-content`}
                id={`analysis-pass-${accordionId}-header`}
                sx={{ backgroundColor: "gray.100" }}
            >
                <Box sx={{ minWidth: 0, width: "100%" }}>
                    <Box
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.75,
                            pr: 1,
                        }}
                    >
                        <Chip
                            size="small"
                            color="primary"
                            label={`Level ${level}`}
                        />
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700 }}
                        >
                            {pass.dimension?.alias ?? "Dimension not recorded"}
                        </Typography>
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`${formatNumber(
                                pass.analyzedCohortCount,
                            )} cohort`}
                        />
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`${results.length} dimension value${
                                results.length === 1 ? "" : "s"
                            }`}
                        />
                        <Chip
                            size="small"
                            color={
                                pass.selection?.shouldContinue
                                    ? "info"
                                    : "success"
                            }
                            variant="outlined"
                            label={
                                pass.selection?.shouldContinue
                                    ? "Continued deeper"
                                    : "Stopped here"
                            }
                        />
                    </Box>
                    {selectedFinding?.claim && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mt: 0.75,
                                overflow: "hidden",
                                pr: 2,
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Selected: {selectedFinding.claim}
                        </Typography>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
                <SectionStack>
                    {pass.analysisScope && (
                        <Paper
                            variant="outlined"
                            sx={{
                                borderColor: "primary.light",
                                borderLeftWidth: 4,
                                p: 1.5,
                            }}
                        >
                            <Field
                                label="Analysis scope"
                                value={pass.analysisScope}
                            />
                        </Paper>
                    )}

                    <CohortPanel
                        title="Analyzed cohort"
                        cohort={pass.analyzedCohort}
                        count={pass.analyzedCohortCount}
                    />

                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <SectionHeading>Selected Dimension</SectionHeading>
                        <Box sx={{ mt: 1 }}>
                            <Field
                                label="Alias"
                                value={pass.dimension?.alias}
                            />
                            <Field
                                label="Reason"
                                value={pass.dimension?.reason}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 0.5, mt: 1 }}
                            >
                                Required filters
                            </Typography>
                            <FilterTable
                                filters={pass.dimension?.requiredFilters}
                            />
                        </Box>
                    </Paper>

                    <Box>
                        <SectionHeading count={results.length}>
                            Analyzed Dimension Values (up to 5)
                        </SectionHeading>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1, mt: 0.25 }}
                        >
                            Distribution is measured across the pass cohort;
                            analyzed is the material supplied to the finding
                            analysis.
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Value</TableCell>
                                        <TableCell>Distribution</TableCell>
                                        <TableCell align="right">
                                            Cohort count
                                        </TableCell>
                                        <TableCell align="right">
                                            Analyzed
                                        </TableCell>
                                        <TableCell align="right">
                                            Findings
                                        </TableCell>
                                        <TableCell align="right">
                                            Material
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.map((result, resultIndex) => {
                                        const share =
                                            result.distribution?.share;
                                        return (
                                            <TableRow key={resultIndex}>
                                                <TableCell>
                                                    {resultValue(
                                                        pass,
                                                        result,
                                                        resultIndex,
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 150 }}>
                                                    <Box
                                                        sx={{
                                                            alignItems: "center",
                                                            display: "flex",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={Math.max(
                                                                0,
                                                                Math.min(
                                                                    100,
                                                                    (share ??
                                                                        0) *
                                                                        100,
                                                                ),
                                                            )}
                                                            sx={{
                                                                flex: 1,
                                                                minWidth: 72,
                                                            }}
                                                        />
                                                        <Typography variant="caption">
                                                            {formatPercent(
                                                                share,
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNumber(
                                                        result.distribution
                                                            ?.count,
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNumber(
                                                        result.analysisMaterial
                                                            ?.length,
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {result.findings?.length ??
                                                        0}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        startIcon={
                                                            <SearchRounded />
                                                        }
                                                        disabled={
                                                            !result
                                                                .analysisMaterial
                                                                ?.length
                                                        }
                                                        onClick={() =>
                                                            onOpenEvidence(
                                                                pass,
                                                                result,
                                                                resultIndex,
                                                            )
                                                        }
                                                    >
                                                        View
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box>
                        <SectionHeading>Findings by Dimension Value</SectionHeading>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                                mt: 1,
                            }}
                        >
                            {results.map((result, resultIndex) => (
                                <FindingsForResult
                                    key={resultIndex}
                                    pass={pass}
                                    result={result}
                                    resultIndex={resultIndex}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Paper
                        variant="outlined"
                        sx={{
                            borderColor: pass.selection?.shouldContinue
                                ? "info.main"
                                : "success.main",
                            borderLeftWidth: 4,
                            p: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                alignItems: "center",
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.75,
                                mb: 0.75,
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700 }}
                            >
                                Pass Decision
                            </Typography>
                            <Chip
                                size="small"
                                color={
                                    pass.selection?.shouldContinue
                                        ? "info"
                                        : "success"
                                }
                                label={
                                    pass.selection?.shouldContinue
                                        ? "Continue to next depth"
                                        : "Complete analysis"
                                }
                            />
                            {pass.selection?.findingId && (
                                <Tooltip title={pass.selection.findingId}>
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`finding ${shortId(
                                            pass.selection.findingId,
                                        )}`}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                        <Field
                            label="Selected claim"
                            value={selectedFinding?.claim}
                        />
                        <Field
                            label="Selection reason"
                            value={pass.selection?.reason}
                        />
                    </Paper>
                </SectionStack>
            </AccordionDetails>
        </Accordion>
    );
}

function FinalOutcome({
    analysis,
}: {
    analysis: KboMultiLevelAnalysisResult;
}) {
    const finding = analysis.finalFinding;
    return (
        <Paper
            variant="outlined"
            sx={{
                borderColor: "success.main",
                borderLeftWidth: 4,
                p: 1.5,
            }}
        >
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    mb: 1,
                }}
            >
                <CheckCircleRounded color="success" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Final Finding
                </Typography>
                {analysis.completionReason && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={analysis.completionReason}
                    />
                )}
                {finding?.findingId && (
                    <Tooltip title={finding.findingId}>
                        <Chip
                            size="small"
                            variant="outlined"
                            label={shortId(finding.findingId)}
                        />
                    </Tooltip>
                )}
            </Box>
            <Field label="Claim" value={finding?.claim} />
            <Field label="Reasoning" value={finding?.reasoning} />
            {!!finding?.representativeConversationIds?.length && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        Representative conversations
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            mt: 0.5,
                        }}
                    >
                        {finding.representativeConversationIds.map(
                            (conversationId) => (
                                <Tooltip
                                    key={conversationId}
                                    title={conversationId}
                                >
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={shortId(conversationId)}
                                    />
                                </Tooltip>
                            ),
                        )}
                    </Box>
                </Box>
            )}
            <Divider sx={{ my: 1.5 }} />
            <CohortPanel title="Final cohort" cohort={analysis.finalCohort} />
        </Paper>
    );
}

function QuestionAnalysis({
    analysis,
    questionIndex,
    onOpenEvidence,
}: {
    analysis: KboMultiLevelAnalysisResult;
    questionIndex: number;
    onOpenEvidence: (
        pass: KboMultiLevelAnalysisPass,
        result: KboAnalysisDimensionResult,
        resultIndex: number,
    ) => void;
}) {
    const sortedPasses = useMemo(
        () =>
            (analysis.passes ?? [])
                .map((pass, originalIndex) => ({ pass, originalIndex }))
                .sort((a, b) => {
                    const levelDifference =
                        (a.pass.level ?? Number.MAX_SAFE_INTEGER) -
                        (b.pass.level ?? Number.MAX_SAFE_INTEGER);
                    return levelDifference || a.originalIndex - b.originalIndex;
                }),
        [analysis.passes],
    );

    return (
        <Card variant="outlined">
            <CardContent>
                <Box
                    sx={{
                        alignItems: "flex-start",
                        display: "flex",
                        gap: 1,
                        mb: 1,
                    }}
                >
                    <Chip
                        size="small"
                        color="primary"
                        label={`Question ${questionIndex + 1}`}
                    />
                    <Typography variant="h6" sx={{ flex: 1 }}>
                        {analysis.question?.text ?? "Question not recorded"}
                    </Typography>
                </Box>
                <Field
                    label="Question rationale"
                    value={analysis.question?.rationale}
                />
                <Field
                    label="Validation reason"
                    value={analysis.question?.validation?.reason}
                />

                <Box sx={{ mt: 1.5 }}>
                    <CohortPanel
                        title="Starting cohort"
                        cohort={analysis.initialCohort}
                        count={analysis.initialCohortCount}
                    />
                </Box>

                <Box sx={{ mt: 2 }}>
                    <SectionHeading count={sortedPasses.length}>
                        Analysis Passes
                    </SectionHeading>
                    <Box
                        sx={{
                            borderLeft: "2px solid",
                            borderColor: "primary.light",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            ml: 1,
                            mt: 1,
                            pl: 1.5,
                        }}
                    >
                        {sortedPasses.map(
                            ({ pass, originalIndex }, displayIndex) => (
                                <PassAccordion
                                    key={pass.passId ?? originalIndex}
                                    pass={pass}
                                    questionIndex={questionIndex}
                                    originalIndex={originalIndex}
                                    displayIndex={displayIndex}
                                    onOpenEvidence={onOpenEvidence}
                                />
                            ),
                        )}
                    </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <FinalOutcome analysis={analysis} />
                </Box>
            </CardContent>
        </Card>
    );
}

export function KboFlowAnalysisResultsSection({
    results,
}: {
    results: KboMultiLevelAnalysisResult[];
}) {
    const [evidenceSelection, setEvidenceSelection] =
        useState<EvidenceSelection | null>(null);
    const [evidencePage, setEvidencePage] = useState(1);

    const openEvidence = (
        pass: KboMultiLevelAnalysisPass,
        result: KboAnalysisDimensionResult,
        resultIndex: number,
    ) => {
        setEvidencePage(1);
        setEvidenceSelection({
            title: `${pass.dimension?.alias ?? "Dimension"} = ${String(
                result.distribution?.value ?? `Result ${resultIndex + 1}`,
            )}`,
            material: result.analysisMaterial ?? [],
        });
    };

    if (results.length === 0) {
        return <Typography variant="body2">No analysis results.</Typography>;
    }

    return (
        <>
            <SectionStack>
                {results.map((analysis, index) => (
                    <QuestionAnalysis
                        key={analysis.question?.id ?? index}
                        analysis={analysis}
                        questionIndex={index}
                        onOpenEvidence={openEvidence}
                    />
                ))}
            </SectionStack>
            <EvidenceDrawer
                selection={evidenceSelection}
                page={evidencePage}
                onPageChange={setEvidencePage}
                onClose={() => setEvidenceSelection(null)}
            />
        </>
    );
}
